import { BrandDetails } from "../types/add-brand-types";

const WEBSITE_CONTEXT_TIMEOUT_MS = 30 * 60 * 1000;

export type WebsiteContextDisplayState =
  | "basic_context"
  | "processing"
  | "ready"
  | "failed"
  | "timed_out";

export interface WebsiteContextDisplay {
  state: WebsiteContextDisplayState;
  label: string;
  tone: "default" | "info" | "success" | "danger" | "warning";
  sourceUrl?: string;
  error?: string;
}

const normalizeStatus = (value?: string | null): string => {
  return (value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
};

const coerceTimestamp = (value?: number | string | null): number | undefined => {
  if (typeof value === "number") {
    return value;
  }
  if (!value) {
    return undefined;
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric;
  }
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? undefined : parsed;
};

export const buildWebsiteContextSourceName = (brandUrl: string): string => {
  const trimmedUrl = brandUrl.trim();
  if (!trimmedUrl) {
    return "Website";
  }

  try {
    const { hostname } = new URL(trimmedUrl);
    const normalizedHost = hostname.replace(/^www\./, "");
    return normalizedHost ? `${normalizedHost} Website` : "Website";
  } catch {
    return "Website";
  }
};

export const getWebsiteContextDisplay = (
  brandData: Pick<
    BrandDetails,
    | "website_context_status"
    | "website_context_source_url"
    | "website_context_error"
    | "website_context_started_at"
    | "website_context_updated_at"
    | "website_context_metadata"
  >
): WebsiteContextDisplay => {
  const metadata = brandData.website_context_metadata ?? {};
  const status = normalizeStatus(
    metadata.status ?? brandData.website_context_status
  );
  const sourceUrl =
    metadata.source_url ?? brandData.website_context_source_url ?? undefined;
  const error =
    metadata.error_message ?? brandData.website_context_error ?? undefined;
  const startedAt = coerceTimestamp(
    metadata.started_at ?? brandData.website_context_started_at
  );
  const isTimedOut =
    status === "processing" &&
    typeof startedAt === "number" &&
    Date.now() - startedAt >= WEBSITE_CONTEXT_TIMEOUT_MS;

  if (isTimedOut || status === "timed_out" || status === "timeout") {
    return {
      state: "timed_out",
      label: "Timed out",
      tone: "warning",
      sourceUrl,
      error,
    };
  }

  if (
    ["processing", "queued", "pending", "running", "in_progress"].includes(
      status
    )
  ) {
    return {
      state: "processing",
      label: "Website processing",
      tone: "info",
      sourceUrl,
    };
  }

  if (
    ["ready", "success", "completed", "complete", "done"].includes(status)
  ) {
    return {
      state: "ready",
      label: "Website context ready",
      tone: "success",
      sourceUrl,
    };
  }

  if (["failed", "error"].includes(status)) {
    return {
      state: "failed",
      label: "Website failed",
      tone: "danger",
      sourceUrl,
      error,
    };
  }

  return {
    state: "basic_context",
    label: "Basic context",
    tone: "default",
    sourceUrl,
  };
};
